/**
 * Consumer Portal — AI Assistant Chat Page.
 */

import AstorIcon from '@mui/icons-material/AutoAwesome';
import ClearIcon from '@mui/icons-material/DeleteOutlined';
import {
  AstorThread,
  type AstorThreadAdapter,
  Box,
  GlassButton,
  GradientText,
  iconSizes,
  ShimmerLoader,
  Typography,
  useI18n,
  useTheme,
} from '@ops-matrix/design-system-ui';
import { PORTAL_AGENT_IDS } from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import type { Message } from '../../hooks/useAgentStream';
import { useAgentStream } from '../../hooks/useAgentStream';
import { useChatHistory, useClearChatHistory, useSaveChatMessage } from '../../lib/trpc/hooks';

const ASTOR_AGENT_ID = PORTAL_AGENT_IDS.CONSUMER_ASSISTANT;

const SUGGESTED_ACTIONS = [
  { key: 'checkStatus', i18nKey: 'chat.checkStatus' as const },
  { key: 'uploadDoc', i18nKey: 'chat.uploadDoc' as const },
  { key: 'whatDocsNeeded', i18nKey: 'chat.whatDocsNeeded' as const },
  { key: 'contactLender', i18nKey: 'chat.contactLender' as const },
] as const;

export default function ChatPage() {
  const { t } = useI18n();
  const theme = useTheme();
  const { consumer } = useConsumerAuth();
  const navigate = useNavigate();

  const historyLoadedRef = useRef(false);
  const conversationIdRef = useRef<string | null>(null);

  const { data: historyData, isLoading: historyLoading } = useChatHistory(ASTOR_AGENT_ID);
  const clearHistoryMutation = useClearChatHistory();
  const saveMessageMutation = useSaveChatMessage();

  useEffect(() => {
    if (historyData?.conversationId) {
      conversationIdRef.current = historyData.conversationId;
    }
  }, [historyData?.conversationId]);

  const initialMessages: Message[] = useMemo(() => {
    if (!historyData?.messages || historyData.messages.length === 0) return [];
    return historyData.messages.map((m) => ({
      id: m.id ? String(m.id) : undefined,
      parentMessageId: m.parentMessageId ?? null,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      streaming: false,
    }));
  }, [historyData?.messages]);

  const handleUserMessage = useCallback(
    (content: string) => {
      saveMessageMutation.mutate(
        {
          agentId: ASTOR_AGENT_ID,
          role: 'user',
          content,
          conversationId: conversationIdRef.current ?? undefined,
        },
        {
          onSuccess: (result) => {
            if (result.conversationId) {
              conversationIdRef.current = result.conversationId;
            }
          },
        },
      );
    },
    [saveMessageMutation],
  );

  const handleAssistantMessage = useCallback(
    (content: string) => {
      saveMessageMutation.mutate({
        agentId: ASTOR_AGENT_ID,
        role: 'assistant',
        content,
        conversationId: conversationIdRef.current ?? undefined,
      });
    },
    [saveMessageMutation],
  );

  const { messages, isStreaming, error, sendMessage, clearMessages, setMessages } = useAgentStream({
    portal: 'consumer',
    onUserMessage: handleUserMessage,
    onAssistantMessage: handleAssistantMessage,
  });

  // Hydrate history once when it arrives.
  useEffect(() => {
    if (!historyLoadedRef.current && initialMessages.length > 0 && messages.length === 0) {
      historyLoadedRef.current = true;
      setMessages(initialMessages);
    }
  }, [initialMessages, messages.length, setMessages]);

  // Keep latest messages reachable from stable callbacks (so AstorThread
  // adapter callbacks don't re-bind the runtime on every re-render).
  const messagesRef = useRef<Message[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ----- AstorThread adapter -----
  const handleSend = useCallback(
    async (text: string) => {
      await sendMessage(ASTOR_AGENT_ID, text, conversationIdRef.current ?? undefined);
    },
    [sendMessage],
  );

  const handleFork = useCallback(
    async (parentMessageId: string, text: string) => {
      await sendMessage(ASTOR_AGENT_ID, text, conversationIdRef.current ?? undefined, parentMessageId);
    },
    [sendMessage],
  );

  const handleReload = useCallback(
    async (parentMessageId: string) => {
      const parent = messagesRef.current.find((m) => m.id === parentMessageId);
      if (!parent || parent.role !== 'user') return;
      await sendMessage(ASTOR_AGENT_ID, parent.content, conversationIdRef.current ?? undefined, parentMessageId);
    },
    [sendMessage],
  );

  const handleCancel = useCallback(() => {
    clearMessages();
  }, [clearMessages]);

  const handleToolNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  const adapter = useMemo<AstorThreadAdapter>(
    () => ({
      messages,
      isStreaming,
      sendMessage: handleSend,
      forkFromMessage: handleFork,
      reloadFromMessage: handleReload,
      cancelStream: handleCancel,
      onToolNavigate: handleToolNavigate,
    }),
    [messages, isStreaming, handleSend, handleFork, handleReload, handleCancel, handleToolNavigate],
  );

  const handleClearHistory = useCallback(() => {
    clearMessages();
    clearHistoryMutation.mutate({ agentId: ASTOR_AGENT_ID });
    conversationIdRef.current = null;
    historyLoadedRef.current = false;
  }, [clearMessages, clearHistoryMutation]);

  const userName = consumer?.name?.split(' ')[0] || '';
  const greetingText = userName ? t('chat.welcome', { name: userName }) : t('chat.welcomeGeneric');

  // Empty-state slot for AstorThread — consumer-specific greeting and the
  // suggested-action chip strip. Suggestion clicks dispatch through the same
  // sendMessage path the composer uses.
  const emptyState = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4, px: 3 }}>
        <AstorIcon sx={{ fontSize: iconSizes['3xl'], color: 'primary.main', opacity: 0.7 }} />
        <GradientText variant="h5" gradient="primary" animated sx={{ fontWeight: 400, textAlign: 'center' }}>
          {greetingText}
        </GradientText>
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          {t('chat.emptyState')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
          {SUGGESTED_ACTIONS.map((action) => (
            <GlassButton
              key={action.key}
              variant="outlined"
              size="small"
              onClick={() => {
                void sendMessage(ASTOR_AGENT_ID, t(action.i18nKey), conversationIdRef.current ?? undefined);
              }}
              disabled={isStreaming}
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 400 }}
            >
              {t(action.i18nKey)}
            </GlassButton>
          ))}
        </Box>
      </Box>
    ),
    [greetingText, t, sendMessage, isStreaming],
  );

  const clearHistoryAction = (
    <GlassButton
      variant="outlined"
      size="small"
      startIcon={<ClearIcon />}
      onClick={handleClearHistory}
      disabled={messages.length === 0 || clearHistoryMutation.isPending}
    >
      {t('chat.clearHistory')}
    </GlassButton>
  );

  return (
    <PageTemplate
      variant="detail"
      titleKey="chat.title"
      descriptionKey="chat.description"
      icon={AstorIcon}
      noPadding
      actions={clearHistoryAction}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 140px)',
          maxHeight: 'calc(100vh - 140px)',
          mx: -3,
          mt: -3,
        }}
      >
        {historyLoading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              py: 4,
            }}
          >
            <ShimmerLoader variant="circular" width={48} height={48} />
            <ShimmerLoader variant="rectangular" width={240} height={20} sx={{ borderRadius: 1 }} />
            <ShimmerLoader variant="rectangular" width={180} height={16} sx={{ borderRadius: 1 }} />
          </Box>
        ) : (
          <AstorThread adapter={adapter} enableVoice emptyState={emptyState} />
        )}

        {error != null && (
          <Box
            sx={{
              mx: 3,
              mb: 1,
              p: 1.5,
              backgroundColor: `${theme.palette.error.main}15`,
              borderRadius: 1,
              border: `1px solid ${theme.palette.error.main}30`,
            }}
          >
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
      </Box>
    </PageTemplate>
  );
}
